import {setItem} from "../helpers/LocalStorage";
import {computed, nextTick, ref} from "vue";
import {useAccountStore} from "../stores/AccountStore.js";
import CompanyApi from "../api/CompanyApi.js";
import EnterpriseApi from "../api/EnterpriseApi.js";

export const useLogInAs = (router) => {
  const loggingInAsCandidate = ref(false);
  const loggingInAsCompanyId = ref(false);
  const loggingInAsEnterpriseId = ref(false);

  const account = useAccountStore();
  const companyApi = new CompanyApi();
  const enterpriseApi = new EnterpriseApi();

  const isLoggingIn = computed(() => account.isSwitchingAccount || loggingInAsCandidate.value || loggingInAsCompanyId.value || loggingInAsEnterpriseId.value);
  let hasRecoveryDestination = false;
  const accountContext = () => `${account.user?.id}:${account.user?.activeCompany?.id}:${account.user?.activeEnterprise?.id}`;

  const switchAccount = async (loading, value, operation) => {
    if (isLoggingIn.value) return;
    const originalContext = accountContext();
    hasRecoveryDestination = false;
    loading.value = value;
    // Suspend and cancel company reads before the server session can change.
    account.isSwitchingAccount = true;
    let completed = false;
    let canResume = true;
    try {
      completed = await operation();
    } finally {
      try {
        if (!completed) {
          const user = await account.fetchUserAccount();
          if (!user) {
            canResume = false;
            window.location.reload();
          } else if (!hasRecoveryDestination && accountContext() !== originalContext) {
            await router.push("/launch-pad");
          }
        }
      } finally {
        // The successful callback resets/remounts the page before reads resume.
        await nextTick();
        if (canResume) account.isSwitchingAccount = false;
        loading.value = false;
      }
    }
  };

  const loginAsCandidate = handleSuccess => switchAccount(loggingInAsCandidate, true, async () => {
    setItem("hideQuickProfile", false, 0.5);
    if (!await setActiveCompanyId(null)) return false;
    if (!await setActiveEnterpriseId(null)) return false;
    await handleSuccess();
    return true;
  });

  const loginAsCompany = (companyId, billableEntityId, handleSuccess) => switchAccount(loggingInAsCompanyId, companyId, async () => {
    setItem("hideQuickProfileCompany", false, 0.5);
    if (!await setActiveEnterpriseId(null)) return false;
    if (!await setActiveCompanyId(companyId, billableEntityId)) return false;
    await handleSuccess();
    return true;
  });

  const loginAsEnterprise = (enterpriseId, handleSuccess) => switchAccount(loggingInAsEnterpriseId, enterpriseId, async () => {
    if (!await setActiveCompanyId(null)) return false;
    if (!await setActiveEnterpriseId(enterpriseId)) return false;
    await handleSuccess();
    return true;
  });

  const setActiveCompanyId = async (companyId, billableEntityId = null) => {
    if (!companyId) companyId = 0;

    try {
      const response = await companyApi.setCompany(companyId);

      const success = response?.success || false;
      if (!success) toast.error("There was an issue logging into that account. Please contact support.");
      return success;
    } catch (e) {
      console.error(e);
      if (billableEntityId) {
        await router.push({path: `/reinstate-payment/${billableEntityId}`});
        hasRecoveryDestination = true;
      }
      else toast.error("There was an issue logging into that account. Please contact support.");
    }
  };

  const setActiveEnterpriseId = async (enterpriseId) => {
    if (!enterpriseId) enterpriseId = 0;

    try {
      const response = await enterpriseApi.setEnterprise(enterpriseId);

      const success = response?.success || false;
      if (!success) toast.error("There was an issue logging into that account. Please contact support.");
      return success;
    } catch (e) {
      console.error(e);
      toast.error("There was an issue logging into that account. Please contact support.");
    }
  };

  return {
    loginAsCandidate,
    loginAsCompany,
    loginAsEnterprise,
    isLoggingIn,
    loggingInAsCandidate,
    loggingInAsCompanyId,
    loggingInAsEnterpriseId,
  };
};
